import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { REFERRAL_TYPES, REFERRAL_TYPE_OPTIONS } from "../../constants/referralTypes";
import { buildQueryParams } from "../../utils/queryString";

const AddOpportunityModal = ({ isOpen, onClose, username, user_id }) => {
  if (!isOpen) return null;

  const BASE = import.meta.env.VITE_BASE_URL;

  // Helper: always get fresh auth headers (no dep headaches)
  const getAuthHeaders = () => {
    const token =
      typeof window !== "undefined" ? localStorage.getItem("token") : null;
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Helper: unify error message extraction
  const extractErr = (e) =>
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    e?.message ||
    "Something went wrong";

  // which flow to show
  const [opportunityType, setOpportunityType] = useState("referral");


  const [formData, setFormData] = useState({
    lead_id: "",
    referralType: "",
    userType: "existing",
    referrerCompanyName: "",
    referrerCompanyId: "",
    referrerContactName: "",
    referrerPhone: "",
    referrerEmail: "",
    referredCompanyName: "",
    referredCompanyId: "",
    referredContactName: "",
    referredPhone: "",
    referredEmail: "",
    description: "",
  });

  const [showFullForm, setShowFullForm] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [similarCompanies, setSimilarCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [errors, setErrors] = useState({});
  const [companyOptions, setCompanyOptions] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({
    success: false,
    message: "",
  });

  // combobox (referrer)
  const [comboboxInput, setComboboxInput] = useState("");
  const [showComboboxDropdown, setShowComboboxDropdown] = useState(false);
  const comboboxRef = useRef(null);

  // =========================
  // 2) BUSINESS OPPORTUNITY STATE
  // =========================
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState("");

  // business combobox
  const [accountInput, setAccountInput] = useState("");
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const accountBoxRef = useRef(null);

  const [oppName, setOppName] = useState("");
  const [refId, setRefId] = useState("");
  const [refIdList, setRefIdList] = useState([]);
  const [reminderTitle, setReminderTitle] = useState("");
  const [reminderAt, setReminderAt] = useState("");
  const [reminderDetails, setReminderDetails] = useState("");

  // close referrer combobox
  useEffect(() => {
    function handleClickOutside(e) {
      if (comboboxRef.current && !comboboxRef.current.contains(e.target)) {
        setShowComboboxDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // close account combobox
  useEffect(() => {
    function handleClickOutside(e) {
      if (accountBoxRef.current && !accountBoxRef.current.contains(e.target)) {
        setShowAccountDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // fetch companies for referral
  useEffect(() => {
    const fetchCompanies = async () => {
      if (!isOpen) return;
      if (opportunityType !== "referral") return;
      if (!formData.referralType) return;

      setLoadingCompanies(true);
      try {
        // Build query parameters based on referral type
        const params = {};

        if (formData.referralType === REFERRAL_TYPES.SPECIAL) {
          params.lead_source = 'Referrer Special';
        } else if (formData.referralType === REFERRAL_TYPES.ACTIVE) {
          params.status = 5;
          params.farmer_id = 'self';
        } else if (formData.referralType === REFERRAL_TYPES.FF) {
          params.lead_source = 'Referrer F&F';
        }

        const queryParams = buildQueryParams(params);
        const queryString = queryParams.toString();
        const endpoint = `${BASE}/api/companies${queryString ? `?${queryString}` : ''}`;
        const response = await axios.get(endpoint, { headers: getAuthHeaders() });

        const companies = (response.data || [])
          .map((company) => ({
            id: company.id,
            name: company.company_name || company.name || "",
          }))
          .filter((company) => company.name);

        setCompanyOptions(companies);
      } catch (error) {
        console.error("Error fetching companies:", error);
      } finally {
        setLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, [isOpen, opportunityType, formData.referralType]); // (BASE removed)

  const filteredCompanies = companyOptions.filter((company) =>
    (comboboxInput || "").length === 0
      ? true
      : company.name.toLowerCase().includes(comboboxInput.toLowerCase())
  );

  const handleReferralChange = (e) => {
    const { name, value } = e.target;

    if (name === "referralType") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        userType:
          prev.userType === "nonexisting" && value !== REFERRAL_TYPES.FF
            ? "existing"
            : prev.userType,
      }));
      if (value) setShowFullForm(true);
      setComboboxInput("");
      setShowComboboxDropdown(false);
    } else if (name === "userType") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        referrerCompanyName: "",
        referrerCompanyId: "",
      }));
      setComboboxInput("");
      setShowComboboxDropdown(false);
    } else if (name === "referredCompanyName") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        referredCompanyId: "",
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateReferralForm = () => {
    const newErrors = {};

    if (!formData.referralType) newErrors.referralType = "Required";
    if (!formData.referrerCompanyName) newErrors.referrerCompanyName = "Required";
    if (!formData.referredCompanyName) newErrors.referredCompanyName = "Required";
    if (formData.referralType === REFERRAL_TYPES.FF && !formData.userType) {
      newErrors.userType = "Required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitOpportunity = async (finalFormData) => {
    try {
      const submissionData = {
        ...finalFormData,
        lead_id: finalFormData.referrerCompanyId || finalFormData.lead_id || "",
      };

      // No query parameters needed for submission - backend handles lead_source internally
      // Query params are only used for filtering companies during company search

      if (submissionData.referralType === REFERRAL_TYPES.FF) {
        if (submissionData.userType === "farmer") {
          await axios.post(
            `${BASE}/api/submit_ff_opp_farmer`,
            submissionData,
            { headers: getAuthHeaders() }
          );
        } else if (submissionData.userType === "existing") {
          await axios.post(
            `${BASE}/api/submit_ff_opp_existing`,
            submissionData,
            { headers: getAuthHeaders() }
          );
        } else if (submissionData.userType === "nonexisting") {
          await axios.post(
            `${BASE}/api/submit_ff_opp_nonexisting`,
            submissionData,
            { headers: getAuthHeaders() }
          );
        } else {
          return { success: false, message: "Invalid user type" };
        }
      } else if (
        submissionData.referralType === REFERRAL_TYPES.ACTIVE ||
        submissionData.referralType === REFERRAL_TYPES.SPECIAL
      ) {
        await axios.post(
          `${BASE}/api/submit_referral_active_special`,
          submissionData,
          { headers: getAuthHeaders() }
        );
      }

      return { success: true, message: "Opportunity created successfully!" };
    } catch (error) {
      console.error("Submission error:", error);

      // Extract field-specific errors from backend
      const backendErrors = error?.response?.data?.errors;
      if (backendErrors && typeof backendErrors === 'object') {
        return {
          success: false,
          message: error?.response?.data?.message || extractErr(error),
          errors: backendErrors,
        };
      }

      return {
        success: false,
        message: extractErr(error),
      };
    }
  };

  const handleReferralSubmit = async (e) => {
    e.preventDefault();

    if (
      (formData.referralType === REFERRAL_TYPES.ACTIVE ||
        formData.referralType === REFERRAL_TYPES.SPECIAL) &&
      !formData.referrerCompanyId
    ) {
      setSubmitStatus({ success: false, message: "Please select a company" });
      return;
    }

    if (!validateReferralForm()) return;

    setIsSubmitting(true);
    setErrors({}); // Clear previous errors

    try {
      if (formData.referralType === REFERRAL_TYPES.FF && formData.userType === "nonexisting") {
        const response = await axios.post(
          `${BASE}/api/companies/fuzzy-search`,
          { query: formData.referrerCompanyName },
          { headers: getAuthHeaders() }
        );

        setSimilarCompanies(response.data);

        if (response.data && response.data.length > 0) {
          setShowConfirmation(true);
          setIsSubmitting(false);
          return;
        }
      }

      const result = await handleSubmitOpportunity(formData);

      // Handle field-specific errors from backend
      if (!result.success && result.errors) {
        setErrors(result.errors);
        setSubmitStatus({ success: false, message: result.message || "Please fix the errors below" });
      } else {
        setSubmitStatus(result);
      }

      if (result.success) {
        setTimeout(() => {
          onClose();
          setSubmitStatus({ success: false, message: "" });
          setSelectedCompany(null);
          setErrors({});
        }, 1500);
      }
    } catch (error) {
      console.error("Error during submission:", error);

      // Handle field-specific errors from backend
      const backendErrors = error?.response?.data?.errors;
      if (backendErrors && typeof backendErrors === 'object') {
        setErrors(backendErrors);
        setSubmitStatus({
          success: false,
          message: error?.response?.data?.message || "Please fix the errors below"
        });
      } else {
        setSubmitStatus({ success: false, message: extractErr(error) });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmation = async (useExisting) => {
    setShowConfirmation(false);
    setIsSubmitting(true);
    setErrors({}); // Clear previous errors

    try {
      let result;

      if (useExisting && selectedCompany) {
        const dataToSubmit = {
          ...formData,
          userType: "existing",
          lead_id: selectedCompany.id,
        };

        await axios.post(`${BASE}/api/submit_ff_opp_existing`, dataToSubmit, {
          headers: getAuthHeaders(),
        });
        result = { success: true, message: "Opportunity created successfully!" };
      } else {
        result = await handleSubmitOpportunity(formData);
      }

      // Handle field-specific errors from backend
      if (!result.success && result.errors) {
        setErrors(result.errors);
        setSubmitStatus({ success: false, message: result.message || "Please fix the errors below" });
      } else {
        setSubmitStatus(result);
      }

      if (result.success) {
        setTimeout(() => {
          onClose();
          setFormData((prev) => ({
            ...prev,
            lead_id: "",
            referralType: "",
            userType: "existing",
            referrerCompanyName: "",
            referrerCompanyId: "",
            referrerContactName: "",
            referrerPhone: "",
            referrerEmail: "",
            referredCompanyName: "",
            referredCompanyId: "",
            referredContactName: "",
            referredPhone: "",
            referredEmail: "",
            description: "",
          }));
          setShowFullForm(false);
          setSelectedCompany(null);
          setComboboxInput("");
          setShowComboboxDropdown(false);
          setErrors({});
        }, 1500);
      }
    } catch (error) {
      console.error("Error during confirmation:", error);

      // Handle field-specific errors from backend
      const backendErrors = error?.response?.data?.errors;
      if (backendErrors && typeof backendErrors === 'object') {
        setErrors(backendErrors);
        setSubmitStatus({
          success: false,
          message: error?.response?.data?.message || "Please fix the errors below"
        });
      } else {
        setSubmitStatus({ success: false, message: extractErr(error) });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // referrer & referred combobox
  const renderCompanyInput = (fieldName) => {
    const isReferrer = fieldName === "referrerCompanyName";
    const idField = isReferrer ? "referrerCompanyId" : "referredCompanyId";

    const shouldUseCombobox =
      (isReferrer &&
        ((formData.referralType === REFERRAL_TYPES.FF &&
          formData.userType !== "nonexisting") ||
          formData.referralType === REFERRAL_TYPES.ACTIVE ||
          formData.referralType === REFERRAL_TYPES.SPECIAL)) ||
      (!isReferrer && companyOptions.length > 0);

    if (shouldUseCombobox) {
      return (
        <div className="mb-4 relative" ref={isReferrer ? comboboxRef : null}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {isReferrer ? "Referrer Company*" : "Referred Company*"}
          </label>
          <input
            type="text"
            name={fieldName}
            value={
              isReferrer
                ? comboboxInput || formData[fieldName] || ""
                : formData[fieldName] || ""
            }
            onChange={(e) => {
              const val = e.target.value;
              if (isReferrer) {
                setComboboxInput(val);
                setShowComboboxDropdown(true);
                setFormData((prev) => ({
                  ...prev,
                  [fieldName]: val,
                  [idField]: "",
                }));
              } else {
                setFormData((prev) => ({
                  ...prev,
                  [fieldName]: val,
                  [idField]: "",
                }));
              }
              if (errors[fieldName]) {
                setErrors((prev) => ({ ...prev, [fieldName]: "" }));
              }
            }}
            onFocus={() => {
              if (isReferrer) setShowComboboxDropdown(true);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-300"
            disabled={loadingCompanies}
            required
            placeholder={
              loadingCompanies
                ? "Loading companies..."
                : "Type to search or select company"
            }
            autoComplete="off"
          />

          {isReferrer &&
            showComboboxDropdown &&
            !loadingCompanies &&
            filteredCompanies.length > 0 && (
              <ul className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-56 rounded-md py-1 text-sm ring-1 ring-black ring-opacity-5 overflow-auto">
                {filteredCompanies.map((company) => (
                  <li
                    key={company.id}
                    className="text-gray-900 cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-100"
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        [fieldName]: company.name,
                        [idField]: company.id,
                        lead_id: company.id,
                      }));
                      setComboboxInput(company.name);
                      setShowComboboxDropdown(false);
                      if (errors[fieldName]) {
                        setErrors((prev) => ({ ...prev, [fieldName]: "" }));
                      }
                    }}
                  >
                    {company.name}
                  </li>
                ))}
              </ul>
            )}

          {isReferrer &&
            showComboboxDropdown &&
            !loadingCompanies &&
            filteredCompanies.length === 0 &&
            (comboboxInput || "").length > 0 && (
              <ul className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-56 rounded-md py-2 text-sm ring-1 ring-black ring-opacity-5">
                <li className="text-gray-500 py-2 pl-3 pr-9">
                  No companies found
                </li>
              </ul>
            )}

          <input type="hidden" name={idField} value={formData[idField]} />
          {errors[fieldName] && (
            <p className="mt-1 text-sm text-red-600">{errors[fieldName]}</p>
          )}
        </div>
      );
    }

    // fallback
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {isReferrer ? "Referrer Company*" : "Referred Company*"}
        </label>
        <input
          type="text"
          name={fieldName}
          value={formData[fieldName]}
          onChange={handleReferralChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-300"
          required
        />
        {errors[fieldName] && (
          <p className="mt-1 text-sm text-red-600">{errors[fieldName]}</p>
        )}
      </div>
    );
  };

  const CompanyTable = ({ companies, selectedCompany, onSelect, highlight }) => (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50 sticky top-0">
        <tr>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Select
          </th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Company
          </th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Contact
          </th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Phone
          </th>
          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Match Score
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {companies.map((company) => (
          <tr
            key={company.id}
            className={`
              ${highlight === "perfect" ? "bg-green-50" : ""}
              ${selectedCompany?.id === company.id ? "bg-blue-50" : ""}
              hover:bg-gray-50
            `}
          >
            <td className="px-4 py-4 whitespace-nowrap">
              <input
                type="radio"
                checked={selectedCompany?.id === company.id}
                onChange={() => onSelect(company)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500"
              />
            </td>
            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
              {company.company_name || company.name}
            </td>
            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
              {company.contact_name || "N/A"}
            </td>
            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
              {company.phone || "N/A"}
            </td>
            <td
              className={`px-4 py-4 whitespace-nowrap text-sm ${
                highlight === "perfect"
                  ? "text-green-700 font-bold"
                  : "text-gray-500"
              }`}
            >
              {company.score ? `${company.score}%` : "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderConfirmationModal = () => {
    const allMatches = [...similarCompanies];
    const perfectMatches = allMatches.filter((c) => c.score === 100);
    const otherMatches = allMatches.filter((c) => c.score < 100);

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] px-4">
        <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[80vh] flex flex-col relative">
          <button
            onClick={() => setShowConfirmation(false)}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            ✕
          </button>

          <h3 className="text-xl font-bold mb-4 text-gray-800">
            Similar Companies
          </h3>

          <div className="flex-1 overflow-y-auto space-y-6">
            {perfectMatches.length > 0 && (
              <div>
                <h4 className="font-bold text-green-700 mb-2">
                  Exact Matches (100%)
                </h4>
                <CompanyTable
                  companies={perfectMatches}
                  selectedCompany={selectedCompany}
                  onSelect={setSelectedCompany}
                  highlight="perfect"
                />
              </div>
            )}

            {otherMatches.length > 0 && (
              <div>
                <h4 className="font-bold text-gray-700 mb-2">
                  Other Similar Companies
                </h4>
                <CompanyTable
                  companies={otherMatches}
                  selectedCompany={selectedCompany}
                  onSelect={setSelectedCompany}
                />
              </div>
            )}

            {allMatches.length === 0 && (
              <p className="text-gray-500 py-4">No similar companies found</p>
            )}
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t mt-4">
            <button
              onClick={() => handleConfirmation(false)}
              className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200"
              disabled={isSubmitting}
            >
              Create New
            </button>
            <button
              onClick={() => handleConfirmation(true)}
              disabled={!selectedCompany || isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Processing..." : "Use Selected"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // fetch accounts for business types
  useEffect(() => {
    if (!isOpen) return;
    if (opportunityType === "referral") return;

    const fetchAccounts = async () => {
      setAccountsLoading(true);
      try {
        let url = "";
        if (opportunityType === "renewal_insurance") {
          url = `${BASE}/api/accounts/by_farmer?funnel=renewal_insurance`;
        } else if (
          opportunityType === "financing" ||
          opportunityType === "insurance"
        ) {
          url = `${BASE}/api/accounts/by_farmer`;
        } else if (opportunityType === "rekyc") {
          url = `${BASE}/api/accounts/re-kyc/by_farmer`;
        }

        if (url) {
          const res = await axios.get(url, { headers: getAuthHeaders() });
          setAccounts(res.data || []);
        } else {
          setAccounts([]);
        }
      } catch (err) {
        console.error("Error fetching accounts:", err);
        setAccounts([]);
      } finally {
        setAccountsLoading(false);
      }
    };

    fetchAccounts();
  }, [isOpen, opportunityType]); // (BASE/token removed)

  // fetch renewal ref IDs
  useEffect(() => {
    const loadRefIds = async () => {
      if (opportunityType !== "renewal_insurance") return;
      if (!selectedAccount) {
        setRefIdList([]);
        setRefId("");
        return;
      }
      try {
        const res = await axios.get(
          `${BASE}/api/insurance/renewal_ref_id?lead_id=${selectedAccount}`,
          { headers: getAuthHeaders() }
        );
        if (Array.isArray(res.data)) {
          setRefIdList(res.data);
          setRefId(res.data[0] || "");
        } else {
          setRefIdList([]);
          setRefId("");
        }
      } catch (err) {
        console.error("Error fetching renewal ref ids:", err);
        setRefIdList([]);
        setRefId("");
      }
    };
    loadRefIds();
  }, [opportunityType, selectedAccount]); // (BASE/token removed)

  const filteredAccounts = accounts.filter((acc) =>
    (accountInput || "").length === 0
      ? true
      : (acc.name || "").toLowerCase().includes(accountInput.toLowerCase())
  );

  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (!selectedAccount) {
        setSubmitStatus({ success: false, message: "Please select an account/lead" });
        return;
      }
      if (!reminderTitle || !reminderAt || !reminderDetails) {
        setSubmitStatus({ success: false, message: "Fill all reminder fields" });
        return;
      }

      const basePayload = {
        lead_id: selectedAccount,
        reminder_title: reminderTitle,
        reminder_at: reminderAt,
        reminder_details: reminderDetails,
      };

      if (opportunityType === "rekyc") {
        await axios.post(`${BASE}/api/products/rekyc`, basePayload, {
          headers: getAuthHeaders(),
        });
      } else if (opportunityType === "financing") {
        if (!oppName.trim()) {
          setSubmitStatus({ success: false, message: "Enter opportunity name" });
          return;
        }
        await axios.post(
          `${BASE}/api/opportunities/add`,
          { ...basePayload, opp_name: oppName },
          { headers: getAuthHeaders() }
        );
      } else if (opportunityType === "insurance") {
        if (!oppName.trim()) {
          setSubmitStatus({ success: false, message: "Enter opportunity name" });
          return;
        }
        if (!refId.trim()) {
          setSubmitStatus({ success: false, message: "Enter Insurance Ref ID" });
          return;
        }
        await axios.post(
          `${BASE}/api/opportunities/insurance`,
          { ...basePayload, opp_name: oppName, ref_id: refId },
          { headers: getAuthHeaders() }
        );
      } else if (opportunityType === "renewal_insurance") {
        if (!oppName.trim()) {
          setSubmitStatus({ success: false, message: "Enter opportunity name" });
          return;
        }
        if (!refId.trim()) {
          setSubmitStatus({ success: false, message: "Select Insurance Ref ID" });
          return;
        }
        await axios.post(
          `${BASE}/api/opportunities/insurance-renewal`,
          { ...basePayload, opp_name: oppName, ref_id: refId },
          { headers: getAuthHeaders() }
        );
      }

      setSubmitStatus({ success: true, message: "Opportunity created" });
      setTimeout(onClose, 1200);
    } catch (err) {
      console.error(err);
      setSubmitStatus({ success: false, message: extractErr(err) });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {submitStatus.message && (
        <div
          className={`fixed top-4 right-4 p-4 rounded-md z-[999] shadow-lg ${
            submitStatus.success
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {submitStatus.message}
        </div>
      )}

      <div
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] px-4 py-6"
      >
        <div
          className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between px-6 py-4 border-b">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">
                Add Opportunity
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            >
              ✕
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Opportunity Type
              </label>
              <select
                value={opportunityType}
                onChange={(e) => {
                  setOpportunityType(e.target.value);
                  setSubmitStatus({ success: false, message: "" });
                }}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-300"
              >
                <option value="financing">Financing</option>
                <option value="insurance">Insurance</option>
                <option value="renewal_insurance">Renewal Insurance</option>
                <option value="rekyc">Re-KYC</option>
                <option value="referral">Referral</option>
              </select>
            </div>

            {opportunityType === "referral" ? (
              <form onSubmit={handleReferralSubmit} className="space-y-6">
                {/* Referral Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Referral Type*
                  </label>
                  <div className="space-y-3">
                    {REFERRAL_TYPE_OPTIONS.map(option => (
                      <label
                        key={option.value}
                        className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.referralType === option.value
                            ? 'border-yellow-400 bg-yellow-50'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <input
                          type="radio"
                          name="referralType"
                          value={option.value}
                          checked={formData.referralType === option.value}
                          onChange={handleReferralChange}
                          className="mt-1 h-4 w-4 text-yellow-400 focus:ring-yellow-300"
                          required
                        />
                        <div className="ml-3 flex-1">
                          <div className="font-medium text-gray-900">{option.label}</div>
                          <div className="text-sm text-gray-600 mt-1">{option.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                  {errors.referralType && (
                    <p className="mt-2 text-sm text-red-600">
                      {errors.referralType}
                    </p>
                  )}
                </div>

                {showFullForm && (
                  <div className="space-y-6">
                    {/* F&F user type */}
                    {formData.referralType === REFERRAL_TYPES.FF && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          User Type*
                        </label>
                        <div className="flex flex-wrap gap-4">
                          {["farmer", "existing", "nonexisting"].map((type) => (
                            <label
                              key={type}
                              className="inline-flex items-center gap-2"
                            >
                              <input
                                type="radio"
                                name="userType"
                                value={type}
                                checked={formData.userType === type}
                                onChange={handleReferralChange}
                                disabled={type === "farmer"}
                                className="h-4 w-4 focus:ring-yellow-300"
                              />
                              <span className="text-gray-700 capitalize">
                                {type === "farmer"
                                  ? "Farmer (disabled)"
                                  : type === "existing"
                                  ? "Existing"
                                  : "Non-existing"}
                              </span>
                            </label>
                          ))}
                        </div>
                        {errors.userType && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors.userType}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Referrer Section */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <h3 className="text-base font-semibold text-gray-800 mb-4">
                        Referrer Information
                      </h3>
                      {renderCompanyInput("referrerCompanyName")}

                      {formData.userType === "nonexisting" && (
                        <>
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Contact Name*
                            </label>
                            <input
                              type="text"
                              name="referrerContactName"
                              value={formData.referrerContactName}
                              onChange={handleReferralChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-300"
                              required
                            />
                            {errors.referrerContactName && (
                              <p className="mt-1 text-sm text-red-600">
                                {errors.referrerContactName}
                              </p>
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Phone Number*
                              </label>
                              <input
                                type="tel"
                                name="referrerPhone"
                                value={formData.referrerPhone}
                                onChange={handleReferralChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-300"
                                required
                              />
                              {errors.referrerPhone && (
                                <p className="mt-1 text-sm text-red-600">
                                  {errors.referrerPhone}
                                </p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Email*
                              </label>
                              <input
                                type="email"
                                name="referrerEmail"
                                value={formData.referrerEmail}
                                onChange={handleReferralChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-300"
                                required
                              />
                              {errors.referrerEmail && (
                                <p className="mt-1 text-sm text-red-600">
                                  {errors.referrerEmail}
                                </p>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Referred Section */}
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <h3 className="text-base font-semibold text-gray-800 mb-4">
                        Referred Information
                      </h3>

                      {renderCompanyInput("referredCompanyName")}
                      {errors.referredCompanyName && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.referredCompanyName}
                        </p>
                      )}

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Contact Name*
                        </label>
                        <input
                          type="text"
                          name="referredContactName"
                          value={formData.referredContactName}
                          onChange={handleReferralChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-300"
                          required
                        />
                        {errors.referredContactName && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors.referredContactName}
                          </p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number*
                          </label>
                          <input
                            type="tel"
                            name="referredPhone"
                            value={formData.referredPhone}
                            onChange={handleReferralChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-300"
                            required
                          />
                          {errors.referredPhone && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.referredPhone}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email*
                          </label>
                          <input
                            type="email"
                            name="referredEmail"
                            value={formData.referredEmail}
                            onChange={handleReferralChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-300"
                            required
                          />
                          {errors.referredEmail && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.referredEmail}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* actions */}
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-3xl hover:bg-gray-100 font-medium"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className={`px-5 py-2 rounded-3xl text-black bg-yellow-300 hover:bg-yellow-400 font-medium ${
                          isSubmitting ? "opacity-75 cursor-not-allowed" : ""
                        }`}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Processing..." : "Submit"}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            ) : (
              <form onSubmit={handleBusinessSubmit} className="space-y-5">
                {/* Account / Lead with search */}
                <div className="relative" ref={accountBoxRef}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Account / Lead *
                  </label>
                  <input
                    value={accountInput}
                    onChange={(e) => {
                      setAccountInput(e.target.value);
                      setShowAccountDropdown(true);
                    }}
                    onFocus={() => setShowAccountDropdown(true)}
                    placeholder={
                      accountsLoading ? "Loading..." : "Type to search account"
                    }
                    className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-yellow-300"
                    autoComplete="off"
                  />
                  {showAccountDropdown &&
                    !accountsLoading &&
                    filteredAccounts.length > 0 && (
                      <ul className="absolute z-20 mt-1 w-full bg-white shadow-lg max-h-56 rounded-md py-1 text-sm ring-1 ring-black/5 overflow-auto">
                        {filteredAccounts.map((acc) => (
                          <li
                            key={acc.lead_id}
                            className="px-3 py-2 hover:bg-yellow-50 cursor-pointer"
                            onClick={() => {
                              setSelectedAccount(acc.lead_id);
                              setAccountInput(acc.name || "");
                              setShowAccountDropdown(false);
                            }}
                          >
                            {acc.name}
                          </li>
                        ))}
                      </ul>
                    )}
                  {showAccountDropdown &&
                    !accountsLoading &&
                    filteredAccounts.length === 0 && (
                      <div className="absolute z-20 mt-1 w-full bg-white shadow-md rounded-md px-3 py-2 text-sm text-gray-400">
                        No account found
                      </div>
                    )}
                </div>

                {(opportunityType === "financing" ||
                  opportunityType === "insurance" ||
                  opportunityType === "renewal_insurance") && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Opportunity Name *
                    </label>
                    <input
                      value={oppName}
                      onChange={(e) => setOppName(e.target.value)}
                      className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-yellow-300"
                    />
                  </div>
                )}

                {(opportunityType === "insurance" ||
                  opportunityType === "renewal_insurance") && (
                  <>
                    {opportunityType === "renewal_insurance" ? (
                      refIdList.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Insurance Ref ID *
                          </label>
                          <select
                            value={refId}
                            onChange={(e) => setRefId(e.target.value)}
                            className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-yellow-300"
                          >
                            <option value="">Select Ref ID</option>
                            {refIdList.map((id, idx) => (
                              <option key={idx} value={id}>
                                {id}
                              </option>
                            ))}
                          </select>
                        </div>
                      )
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Insurance Ref ID *
                        </label>
                        <input
                          value={refId}
                          onChange={(e) => setRefId(e.target.value)}
                          className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-yellow-300"
                          placeholder="Enter insurance reference ID"
                        />
                      </div>
                    )}
                  </>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reminder Title *
                    </label>
                    <input
                      value={reminderTitle}
                      onChange={(e) => setReminderTitle(e.target.value)}
                      className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-yellow-300"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reminder At *
                    </label>
                    <input
                      type="datetime-local"
                      value={reminderAt}
                      onChange={(e) => setReminderAt(e.target.value)}
                      className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-yellow-300"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reminder Details *
                  </label>
                  <input
                    value={reminderDetails}
                    onChange={(e) => setReminderDetails(e.target.value)}
                    className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-yellow-300"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 bg-white border rounded-3xl hover:bg-gray-100"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-yellow-300 rounded-3xl disabled:opacity-60 hover:bg-yellow-400"
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {showConfirmation && renderConfirmationModal()}
    </>
  );
};

export default AddOpportunityModal;
